import React, { useEffect } from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalContent = styled.div`
  background-color: #2c1249;
  border-radius: 20px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 30px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  position: relative;
  color: #ffffff;
  text-align: left;
`;

const CloseButton = styled.span`
  position: absolute;
  top: 20px;
  right: 25px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 28px;
  cursor: pointer;
  transition: color 0.3s ease;
  
  &:hover {
    color: #ff3eb5;
  }
`;

const ModalTitle = styled.h2`
  color: #ff3eb5;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  text-align: left;
`;

const InfoSection = styled.div`
  margin-bottom: 25px;
  text-align: left;
`;

const SectionTitle = styled.h3`
  color: #ffbe00;
  margin-bottom: 15px;
  font-size: 1.2rem;
  text-align: left;
`;

const SectionText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  margin-bottom: 10px;
  text-align: left;
`;

const SectionList = styled.ul`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  padding-left: 20px;
  text-align: left;
  
  li {
    margin-bottom: 10px;
    text-align: left;
  }
`;

const InfoModal = ({ onClose, title, content }) => {
  // Blocca lo scroll del body quando la modale è aperta
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Chiude la modale quando si preme ESC
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [onClose]);

  // Renderizza contenuto custom se fornito
  if (title && content) {
    return (
      <ModalOverlay onClick={onClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <CloseButton onClick={onClose}>&times;</CloseButton>
          <ModalTitle>{title}</ModalTitle>
          <div style={{ textAlign: 'left' }}>{content}</div>
        </ModalContent>
      </ModalOverlay>
    );
  }

  // Altrimenti renderizza il modal informativo predefinito
  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        <ModalTitle>Service Information</ModalTitle>
        
        <InfoSection>
          <SectionTitle>What the service does</SectionTitle>
          <SectionText>
            This service analyzes publicly available information on the provided Instagram profile 
            (bio, profile picture, visible highlights, and recent feed of public posts).
          </SectionText>
          <SectionText>
            We use artificial intelligence to identify patterns, recurring themes, and non-verbal 
            signals present in these elements.
          </SectionText>
        </InfoSection>
        
        <InfoSection>
          <SectionTitle>What the service does NOT do</SectionTitle>
          <SectionList>
            <li>This is NOT a clinical or definitive psychological profile.</li>
            <li>It does NOT have access to private or non-public information.</li>
            <li>It CANNOT know the person in their entirety or outside of their online presence.</li>
            <li>The analyses are based on inferences and probabilities derived from public data, not on absolute certainties.</li>
          </SectionList>
        </InfoSection>
        
        <InfoSection>
          <SectionTitle>Limits of the analysis</SectionTitle>
          <SectionList>
            <li>The image that a person chooses to project on social media is often curated and does not necessarily reflect every aspect of their personality or real life.</li>
            <li>The interpretation of symbols, short texts, and images can be subjective and dependent on cultural context.</li>
            <li>The service does not take into account external factors, mood changes, or personal experiences that are not publicly shared.</li>
          </SectionList>
        </InfoSection>
        
        <InfoSection>
          <SectionTitle>How to interpret the results</SectionTitle>
          <SectionList>
            <li>Consider the generated profile as a possible interpretation or a basic hypothesis.</li>
            <li>Use it as a starting point to understand what might be the interests, values, or communication style that the person chooses to show online.</li>
            <li>The approach suggestions are based on these hypotheses and aim to facilitate respectful and genuine interaction.</li>
          </SectionList>
        </InfoSection>
        
        <InfoSection>
          <SectionTitle>Ethical Principles and Responsible Use</SectionTitle>
          <SectionList>
            <li>This tool must be used in an ethical way that respects others' privacy.</li>
            <li>The information provided is based exclusively on public data that the person has voluntarily chosen to share.</li>
            <li>DO NOT use this information for stalking, harassment, discrimination, or making hasty and definitive judgments about the person.</li>
            <li>The purpose is to improve your understanding for potential interaction, not to provide a complete dossier on someone.</li>
            <li>Always remember that every individual is complex and deserves to be known in person, beyond their digital footprint.</li>
          </SectionList>
        </InfoSection>
        
        <InfoSection>
          <SectionTitle>Legal Disclaimer</SectionTitle>
          <SectionText>
            The service provides analyses based on algorithms and public data. We assume no responsibility for the absolute accuracy of the inferences or for the use that the user makes of the information provided.
          </SectionText>
        </InfoSection>
      </ModalContent>
    </ModalOverlay>
  );
};

export default InfoModal; 